import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { AppState, useAppDispatch } from 'state'
import { addressesEqual } from 'utils/addressHelpers'
import { useIsAuthenticated } from 'state/profile/hooks'
import { FetchStatus, HolderBonus, State, UserBonuses } from 'state/types'
import {
  fetchFarmingLevels,
  fetchUserBonuses,
  fetchUserBonusesNextReward,
  fetchUserBonusesTotalEarned,
  fetchUserFarmingLevel,
  fetchUserHolderBonuses,
  fetchUserRewardsTotalEarned,
  resetUser,
} from '.'

export const useAffiliate = () => {
  const dispatch = useAppDispatch()
  const isAuthenticated = useIsAuthenticated()

  useEffect(() => {
    dispatch(fetchFarmingLevels())
  }, [dispatch])

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserFarmingLevel())
      dispatch(fetchUserBonuses())
      dispatch(fetchUserBonusesNextReward())
      dispatch(fetchUserBonusesTotalEarned())
      dispatch(fetchUserHolderBonuses())
      dispatch(fetchUserRewardsTotalEarned())
    } else {
      dispatch(resetUser())
    }
  }, [isAuthenticated, dispatch])
}

export const useIsHolderBonusExists = (address: string): boolean => {
  const { farms } = useSelector((state: State) => state.affiliate.user.holderBonus)

  return useMemo(() => farms.some(({ farmAddress }) => addressesEqual(farmAddress, address)), [farms, address])
}

const emptyHolderBonus: HolderBonus = {
  farmAddress: '',
  currentBonus: 0,
  nextBonus: 0,
  currentNumberOfDays: 0,
  nextLevelNumberOfDays: 0,
  currentRewardInNmx: 0,
  totalEarned: 0,
  unclaimed: 0,
  poolShare: 0,
}

export const useHolderBonusData = () => {
  const isUnclaimedLoading = useSelector(
    (state: AppState) => state.affiliate.user.bonuses.fetchStatus !== FetchStatus.FETCHED,
  )
  const isTotalEarnedLoading = useSelector(
    (state: AppState) => state.affiliate.user.bonuses.totalEarned.fetchStatus !== FetchStatus.FETCHED,
  )
  const isHolderBonusLoading = useSelector(
    (state: AppState) => state.affiliate.user.holderBonus.fetchStatus !== FetchStatus.FETCHED,
  )
  const { farms } = useSelector((state: State) => state.affiliate.user.holderBonus)

  const getHolderBonus = (address: string): HolderBonus =>
    farms.find(({ farmAddress }) => addressesEqual(farmAddress, address)) || emptyHolderBonus

  return {
    getHolderBonus,
    isHolderBonusLoading,
    isTotalEarnedLoading,
    isUnclaimedLoading,
  }
}

export const useCurrentFarmingLevel = () =>
  useSelector((state: State) => {
    const { farmingLevels, user } = state.affiliate
    return farmingLevels.find(({ name }) => name === user.farmingLevel.currentLevel) ?? farmingLevels[0]
  })

type Bonuses = {
  cashback: {
    unclaimed: UserBonuses['cashback']
    nextReward: UserBonuses['cashback']
    totalEarned: UserBonuses['cashback']
  }
  referralFarming: {
    unclaimed: UserBonuses['referralFarming']
    nextReward: UserBonuses['referralFarming']
    totalEarned: UserBonuses['referralFarming']
  }
  referralTrading: {
    unclaimed: UserBonuses['referralTrading']
    nextReward: UserBonuses['referralTrading']
    totalEarned: UserBonuses['referralTrading']
  }
  teamFarming: {
    unclaimed: UserBonuses['teamFarming']
    nextReward: UserBonuses['teamFarming']
    totalEarned: UserBonuses['teamFarming']
  }
  teamTrading: {
    unclaimed: UserBonuses['teamTrading']
    nextReward: UserBonuses['teamTrading']
    totalEarned: UserBonuses['teamTrading']
  }
}

export const useBonuses = () =>
  useSelector((state: State) => {
    const { bonuses } = state.affiliate.user

    return ['cashback', 'referralFarming', 'referralTrading', 'teamFarming', 'teamTrading'].reduce<Bonuses>(
      (acc, fieldName) => ({
        [fieldName]: {
          unclaimed: bonuses[fieldName],
          nextReward: bonuses.nextReward[fieldName],
          totalEarned: bonuses.totalEarned[fieldName],
        },
        ...acc,
      }),
      {} as Bonuses,
    )
  })
